import { all, takeLatest, put, call, select } from 'redux-saga/effects';

import {
  actions,
  types,
  getPostId,
  getPost,
  getSelectedConceptId,
  getCurrentQuiz,
  getQuizzes,
  getCurrentQuizIndex,
  transferAuthorAvatar,
} from '@blueberry/redux/post';
import { getIsInapp, getInappQuery } from '@blueberry/redux/location';
import { getRegion } from '@blueberry/redux/locale';
import { getUserID } from '@blueberry/redux/profile';
import { getDeviceId } from '@blueberry/redux/auth';
import { sendGTMEvent } from '@blueberry/redux/googleTagManager';
import { actions as errorActions } from '@blueberry/redux/error';

import * as apis from '@blueberry/services/contents';
import { appendUserTrackEvent } from '@blueberry/services/local';
import {
  fetchQuizzesHistory,
  fetchNextQuiz,
  postQuizAnswer,
  fetchQuiz,
} from '@blueberry/services/apis';

import { LIKE, VISIT } from '@blueberry/constants/recordEventType';
import { SOURCE_DIRECT, MEDIUM_ORGANIC } from '@blueberry/constants/utm';
import { PAGELOAD } from '@blueberry/constants/eventNames';
import {
  TYPE,
  CURRICULUM,
  SUBJECT,
  SUBTOPIC,
} from '@blueberry/constants/gtmGroups';
import { POST_VIEW, QUIZ_SUBMIT } from '@blueberry/constants/gtmEvents';
import { QUIZ_INDEX } from '@blueberry/constants/gtmVariables';
import { SKIPPED } from '@blueberry/constants/quizStatus';

export function* getTrackData({ webPlatform, referrer, url }) {
  const inappQuery = yield select(getInappQuery);
  const webRegion = yield select(getRegion);
  const userId = yield select(getUserID);
  const deviceId = yield select(getDeviceId);
  const device = inappQuery.device;

  const platform = device || webPlatform;
  // utm data logic
  // 1. query string !== null, use query string
  // 2. referrer !== null, utm_source = URL, utm_medium = 'organic'
  // 3. other case, utm_source = 'direct', utm_medium = undefined
  const utmSource = inappQuery.utmSource || referrer || SOURCE_DIRECT;
  const utmMedium = inappQuery.utmMedium || (referrer && MEDIUM_ORGANIC);

  const trackData = {
    region: device ? inappQuery.region : webRegion,
    platform,
    userId: device ? inappQuery.id : userId,
    // can't get deviceId on web view in other app, set a const value
    deviceId: device ? undefined : deviceId || 'incognito',
    eventName: PAGELOAD,
    utmSource,
    utmMedium,
    utmCampaign: inappQuery.utmCampaign,
    utmContent: inappQuery.utmContent,
    url,
  };

  return trackData;
}

export function* handleGTMGroup(post) {
  const region = yield select(getRegion);

  const data = {
    [TYPE]: post.type,
    [CURRICULUM]: undefined,
    [SUBJECT]: undefined,
    [SUBTOPIC]: undefined,
  };

  if (post.curriculumName) {
    data[CURRICULUM] = `${region}-${post.curriculumId}`;
  }
  if (post.subjectName) {
    data[SUBJECT] = `${post.subjectId}-${post.subjectName}`;
  }
  if (post.subtopicName) {
    data[SUBTOPIC] = post.subtopicName;
  }

  yield put(sendGTMEvent(POST_VIEW, data));
}

export function* fetchPostFlow(action) {
  try {
    const { id: titleId } = action.payload;
    // format: title-id
    const id = titleId.split('-').pop();
    const response = yield call(apis.getPost, {
      id,
    });

    const post = apis.transferPostObject(response);
    const avatarResponse = yield call(apis.getAuthorAvatar, {
      id: post.authorId,
    });
    const { profilePictureUrl: authorAvatar } = transferAuthorAvatar(
      avatarResponse
    );
    const relatedPosts = (response.related_posts || []).map(p =>
      apis.transferPostWithoutDescription(p)
    );

    if (post.concepts.length > 0) {
      const conceptId = post.concepts[0].qms_id;
      yield put(actions.changeConcept(conceptId));
    }

    yield put(
      actions.fetchPostSuccess({
        post,
        relatedPosts,
        authorAvatar,
      })
    );
  } catch (err) {
    yield put(actions.fetchPostFailure());
  }
}

export function* toggleFavorite() {
  const id = yield select(getPostId);
  yield call(apis.postRecordEvent, {
    id,
    eventType: LIKE,
  });
}

export function* postEventFlow(action) {
  try {
    const { trackData } = action.payload;

    const post = yield select(getPost);
    // in case run twice at prefetch and didMount
    const track = yield call(getTrackData, trackData);
    appendUserTrackEvent({
      ...track,
      type: post.type,
    });

    yield call(handleGTMGroup, post);

    const isInapp = yield select(getIsInapp);
    if (!isInapp) {
      yield call(apis.postRecordEvent, {
        id: post.id,
        eventType: VISIT,
      });
    }
  } catch (err) {
    errorActions.sendError(err);
  }
}

function clearSkippedQuizStatus(quiz) {
  if (quiz.status === SKIPPED) {
    delete quiz.answer;
    delete quiz.status;
  }
}

export function* fetchQuizHistoryFlow() {
  const conceptId = yield select(getSelectedConceptId);
  const userId = yield select(getUserID);
  const [{ quizzes }, { qzs, left_in_concept: leftInConcept }] = yield all([
    call(fetchQuizzesHistory, { userId, conceptId }),
    call(fetchNextQuiz, { userId, conceptId }),
  ]);

  const hasNextQuiz = leftInConcept > 0;

  if (qzs[0]) {
    quizzes.push(qzs[0]);
  }
  /*
    user has done all quizzes of this concept, and can't get any more quiz
    so we need to fetch the quiz object
    all behvior write at test case
  */
  if (!hasNextQuiz && !qzs.length) {
    const selectedQuiz = quizzes[quizzes.length - 1];
    const quiz = yield call(fetchQuiz, selectedQuiz.quiz_id);
    quizzes[quizzes.length - 1] = {
      ...selectedQuiz,
      ...quiz,
    };

    clearSkippedQuizStatus(quizzes[quizzes.length - 1]);
  }

  yield put(actions.fetchQuizHistorySuccess({ quizzes, hasNextQuiz }));
}

function checkIsQuiz(quiz) {
  return quiz.hasOwnProperty('correct_ans');
}

export function* fetchNextQuizFlow() {
  try {
    const quizzes = yield select(getQuizzes);
    const currentIndex = yield select(getCurrentQuizIndex);
    const isLatestQuiz = currentIndex === quizzes.length - 1;

    if (!isLatestQuiz) {
      yield put(actions.changeQuizIndex(currentIndex + 1));
      return;
    }

    const conceptId = yield select(getSelectedConceptId);
    const userId = yield select(getUserID);

    const { qzs, left_in_concept: leftInConcept } = yield call(fetchNextQuiz, {
      userId,
      conceptId,
    });

    const hasNextQuiz = leftInConcept > 0;

    yield put(actions.fetchNextQuizSuccess({ quiz: qzs[0], hasNextQuiz }));
  } catch (err) {
    yield put(errorActions.sendError(err));
  }
}

export function* postQuizAnswerFlow(action) {
  try {
    const { answer, isCorrect } = action.payload;
    const { qz_id: quizId, concepts } = yield select(getCurrentQuiz);
    const userId = yield select(getUserID);
    const currentIndex = yield select(getCurrentQuizIndex);

    const concept_ids = concepts.map(p => p['concept_id']);

    const data = {
      answer,
      pass: isCorrect,
      concept_ids,
    };

    yield put(
      sendGTMEvent(QUIZ_SUBMIT, {
        [QUIZ_INDEX]: currentIndex + 1,
      })
    );

    yield call(postQuizAnswer, { userId, quizId, data });
    yield put(actions.postQuizAnswerSuccess());
  } catch (err) {
    yield put(errorActions.sendError(err));
  }
}

export function* changeQuizIndexFlow(action) {
  try {
    const { index } = action.payload;

    const quizzes = yield select(getQuizzes);
    const quiz = quizzes[index];

    clearSkippedQuizStatus(quiz);

    if (!checkIsQuiz(quiz)) {
      const response = yield call(fetchQuiz, quiz.quiz_id);

      quizzes[index] = { ...quiz, ...response };
    }

    yield put(actions.changeQuizIndexSuccess(quizzes, index));
  } catch (err) {
    yield put(errorActions.sendError(err));
  }
}

export default function*() {
  yield all([
    takeLatest(types.FETCH_POST, fetchPostFlow),
    takeLatest(types.TOGGLE_FAVORITE, toggleFavorite),
    takeLatest(types.POST_EVENT, postEventFlow),
    takeLatest(types.FETCH_QUIZ_HISTORY, fetchQuizHistoryFlow),
    takeLatest(types.FETCH_NEXT_QUIZ, fetchNextQuizFlow),
    takeLatest(types.POST_QUIZ_ANSWER, postQuizAnswerFlow),
    takeLatest(types.CHANGE_QUIZ_INDEX, changeQuizIndexFlow),
  ]);
}



// WEBPACK FOOTER //
// ./app/sagas/post.js